import { useEffect, useState } from 'react';

import { useTrackMap } from '../TrackMapContext';

// Screen point (CSS px, canvas-relative) sampled under the header title to decide
// whether the backdrop behind the floating header is dark or light.
const SAMPLE_POINT = { x: 60, y: 44 };

// Re-sample at most this often during continuous renders (panning, tile loads) —
// a forced sample on moveend always captures the final frame regardless.
const SAMPLE_THROTTLE_MS = 200;

// Perceived-luminance cutoff (0-255): below = dark backdrop → white title.
const DARK_LUMINANCE_THRESHOLD = 128;

/**
 * Classes for the floating Track header (`radar-header-blur`) that adapt it to the map
 * behind it. Shared by the list map view and the detail page so both headers restyle
 * identically — mirrors wal-frontend-v2's useRadarHeaderTheme.
 *
 * - `radar-header-blur-transparent`: Streets + Satellite + Outdoors themes drop the white
 *   wash for a faint white border ('streets' also matches satellite-streets-v12); only
 *   the Light theme keeps the default white wash.
 * - `radar-header-light-content`: white title/icon while the map pixel behind the title
 *   is dark (satellite imagery, or the dark out-of-world backdrop at high latitudes).
 *   The color is READ from the rendered canvas rather than derived from zoom/style —
 *   the dark backdrop's position depends on pan position, not just zoom level.
 * - `radar-header-theme-{light|streets|satellite|outdoors}` + `radar-header-proj-{flat|globe}`:
 *   drive the header's border-bottom color, which differs per base style AND per projection
 *   (see app.scss). Projection is list-page-only: the list passes its live map projection,
 *   the detail page always passes `'mercator'` (flat) because it never renders in globe.
 */

// Map a Mapbox base-style URL to the short theme key used in the border-color CSS classes.
const styleToThemeKey = (mapStyle: string | undefined): 'light' | 'streets' | 'satellite' | 'outdoors' => {
    // Order matters: 'satellite-streets-v12' contains 'streets', so match satellite first.
    if (mapStyle?.includes('satellite')) return 'satellite';
    if (mapStyle?.includes('streets')) return 'streets';
    if (mapStyle?.includes('outdoors')) return 'outdoors';

    return 'light';
};

const useTrackHeaderTheme = (projection: 'mercator' | 'globe' = 'mercator'): string => {
    const { mapRef, isMapReady, mapStyle } = useTrackMap();
    const isTransparentHeader = Boolean(mapStyle?.includes('streets') || mapStyle?.includes('outdoors'));
    const [isDarkBackdrop, setIsDarkBackdrop] = useState(false);

    useEffect(() => {
        const map = mapRef.current;
        if (!isMapReady || !map || !isTransparentHeader) return;

        let lastSampleAt = 0;
        let forceSample = true;

        // Must run inside a `render` frame: the map canvas has preserveDrawingBuffer
        // off, so outside the frame the buffer is already cleared and readPixels
        // would return black regardless of what's on screen.
        const onRender = () => {
            const now = performance.now();
            if (!forceSample && now - lastSampleAt < SAMPLE_THROTTLE_MS) return;
            forceSample = false;
            lastSampleAt = now;

            const canvas = map.getCanvas();
            const gl = (canvas.getContext('webgl2') || canvas.getContext('webgl')) as WebGLRenderingContext | null;
            if (!gl) return;

            const dpr = window.devicePixelRatio || 1;
            const x = Math.min(Math.round(SAMPLE_POINT.x * dpr), canvas.width - 1);
            // WebGL's y origin is bottom-left, CSS's is top-left — flip it.
            const y = Math.max(canvas.height - Math.round(SAMPLE_POINT.y * dpr), 0);
            const pixel = new Uint8Array(4);
            gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);

            // ITU-R BT.601 perceived luminance.
            const luminance = 0.299 * pixel[0] + 0.587 * pixel[1] + 0.114 * pixel[2];
            setIsDarkBackdrop(luminance < DARK_LUMINANCE_THRESHOLD);
        };

        // Camera settled — force a fresh sample of the final frame (triggerRepaint
        // guarantees one more render even if Mapbox considers itself idle).
        const onMoveEnd = () => {
            forceSample = true;
            map.triggerRepaint();
        };

        map.on('render', onRender);
        map.on('moveend', onMoveEnd);
        onMoveEnd();

        return () => {
            map.off('render', onRender);
            map.off('moveend', onMoveEnd);
        };
        // mapStyle re-arms the sampler after a theme switch: the style swap repaints
        // the whole canvas without a camera move, so moveend alone would miss it.
    }, [isMapReady, mapRef, isTransparentHeader, mapStyle]);

    const isLightContent = isTransparentHeader && isDarkBackdrop;
    const themeKey = styleToThemeKey(mapStyle);
    const projectionKey = projection === 'globe' ? 'globe' : 'flat';

    return [isTransparentHeader ? 'radar-header-blur-transparent' : '', isLightContent ? 'radar-header-light-content' : '', `radar-header-theme-${themeKey}`, `radar-header-proj-${projectionKey}`]
        .filter(Boolean)
        .join(' ');
};

export default useTrackHeaderTheme;
